import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Event } from '../../../types/event.types';
import { Icon, Button } from '../../../pages/DesignSystem';
import EventForm from '../EventForm/EventForm';
import EventLivePreview from './EventLivePreview';
import { FormData } from '../EventForm/types';
import { supabaseEventService } from '../../../supabase/event.service';
import { SecurityUtils } from '../../../utils/security';
import { validateServerSide, eventSchema } from '../../../utils/serverValidation';
import './EventFormModal.css';

interface EventFormModalProps {
  isOpen: boolean;
  event?: Event;
  organizerId: string;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  onDuplicate?: (duplicatedEvent: Partial<Event>) => void;
  isEditingOccurrence?: boolean;
}

/**
 * EventFormModal - Modern modal for creating/editing events
 * Split layout: Form on left, Live Preview on right
 */
const EventFormModal: React.FC<EventFormModalProps> = ({
  isOpen,
  event,
  organizerId,
  onClose,
  onSubmitSuccess,
  onDuplicate,
  isEditingOccurrence = false
}) => {
  // Track form data for live preview
  const [previewData, setPreviewData] = useState<Partial<FormData>>({});
  const [currentStep, setCurrentStep] = useState(0);

  // Handle form data changes for live preview
  const handleFormDataChange = useCallback((data: Partial<FormData>) => {
    setPreviewData(data);
  }, []);

  // Handle step changes
  const handleStepChange = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  // Handle form submission - save to database (same as EventFormSidebar)
  const handleSubmit = useCallback(async (eventData: Partial<Omit<Event, 'id'>> & { id?: string }) => {
    try {
      // Sanitize all text inputs
      const sanitizedEventData = {
        ...eventData,
        title: SecurityUtils.sanitizeInputForSubmission(eventData.title || ''),
        description: SecurityUtils.sanitizeInputForSubmission(eventData.description || ''),
        location: SecurityUtils.sanitizeInputForSubmission(eventData.location || ''),
        locationName: SecurityUtils.sanitizeInputForSubmission(eventData.locationName || ''),
        ticketingUrl: eventData.ticketingUrl ? SecurityUtils.sanitizeInputForSubmission(eventData.ticketingUrl) : undefined,
        prices: eventData.prices?.map(price => ({
          ...price,
          description: SecurityUtils.sanitizeInputForSubmission(price.description || '')
        }))
      };

      // Server-side validation
      const validationMode = sanitizedEventData.status === 'draft' ? 'draft' : 'publish';
      const serverValidation = await validateServerSide(sanitizedEventData, eventSchema, validationMode);
      if (!serverValidation.success) {
        toast.error('Validation failed. Please check your inputs.');
        console.error('Server validation failed:', serverValidation.errors);
        return;
      }

      const eventWithOrganizer = {
        ...sanitizedEventData,
        organizerId
      };

      // Create or update event in database
      if (event?.id) {
        const { id, ...updateData } = eventWithOrganizer;
        await supabaseEventService.updateEvent(event.id, updateData);
        console.log('✅ Event updated successfully');
      } else {
        await supabaseEventService.createEvent(eventWithOrganizer);
        console.log('✅ Event created successfully');
      }

      onSubmitSuccess?.();
    } catch (error) {
      toast.error('Error saving event. Please try again.');
      console.error('Error saving event:', error);
    }
  }, [event, organizerId, onSubmitSuccess]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isEditing = !!event?.id;
  const modalTitle = isEditing ? 'Edit Event' : 'Create an Event';

  return (
    <div className="desktop-event-form__backdrop" onClick={handleBackdropClick}>
      <div className="desktop-event-form__container">
        {/* Modal Header */}
        <header className="desktop-event-form__header">
          <div className="desktop-event-form__header-left">
            <h2 className="desktop-event-form__title">{modalTitle}</h2>
            {isEditing && onDuplicate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const { id, ...duplicatedEvent } = event!;
                  onDuplicate(duplicatedEvent);
                }}
              >
                <Icon name="Copy" size={16} />
                Duplicate
              </Button>
            )}
          </div>
          <button
            className="desktop-event-form__close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <Icon name="X" size={24} />
          </button>
        </header>

        {/* Modal Content - Split Layout */}
        <div className="desktop-event-form__content">
          {/* Left Side - Form */}
          <div className="desktop-event-form__form-section">
            <EventForm
              event={event}
              organizerId={organizerId}
              onSubmit={handleSubmit}
              onClose={onClose}
              isEditingOccurrence={isEditingOccurrence}
              mode="modal"
              onFormDataChange={handleFormDataChange}
              onStepChange={handleStepChange}
              hideStepperAndPreview={true}
            />
          </div>

          {/* Right Side - Live Preview */}
          <div className="desktop-event-form__preview-section">
            <div className="desktop-event-form__preview-header">
              <Icon name="Eye" size={18} />
              <span>Live Preview</span>
            </div>
            <div className="desktop-event-form__preview-content">
              <EventLivePreview
                formData={previewData}
                currentStep={currentStep}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventFormModal;
